import { sleep } from '@dealstack/shared/lib/promise';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { runViewTransition } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/viewTransition.utils';
import { COMPLETE_REDIRECT_DELAY_MS } from '../../Ingestion/ingestion.constants';
import { prefetchResultsLanding } from '../hooks/prefetchResultsLanding';
import { categoryPath } from '../workspacePaths.utils';
import { carryOverSearch, WORKSPACE_PARAMS } from '../workspaceUrlState.utils';
import type { CompletedRunHandOffOptions } from './useCompletedRunHandOff.types';

/**
 * Completed-run hand-off, shared by the modal and the in-page view: the finished checklist holds while the
 * landing prefetches, then one cross-fade opens Securities with the category panel collapsed.
 */
export const useCompletedRunHandOff = ({
	params,
	isComplete,
	searchParams,
	replace,
}: CompletedRunHandOffOptions): void => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { dealId, batchId } = params;
	const sticky = carryOverSearch(searchParams);

	// Prefetching and navigating are the side effects here.
	useEffect(() => {
		if (!isComplete) {
			return undefined;
		}
		let cancelled = false;
		const batch = { dealId, batchId };
		const landing = 'securities';
		const search = carryOverSearch(new URLSearchParams(sticky), {
			[WORKSPACE_PARAMS.panel]: 'collapsed',
		});

		void Promise.all([
			sleep(COMPLETE_REDIRECT_DELAY_MS),
			prefetchResultsLanding(queryClient, batch, search),
		]).then(() => {
			if (cancelled) {
				return;
			}
			runViewTransition(() =>
				navigate(`${categoryPath(dealId, batchId, landing)}${search}`, { replace })
			);
		});
		return () => {
			cancelled = true;
		};
	}, [isComplete, dealId, batchId, sticky, replace, queryClient, navigate]);
};
